import { useMemo } from 'react';

import windowWrapper from './window-wrapper';

// Internal API not meant to be exported, used by useApi().
export class Api {
  private readonly BASE_URL: string = windowWrapper.get<string>('api_base_url') || '';
  private csrfToken: string | undefined;

  constructor() {
    this.csrfToken = windowWrapper.get<string | undefined>('csrf_token');
  }

  static hasJwtAuthToken(): boolean {
    return Boolean(localStorage.getItem('access_token'));
  }

  // Wrapper around fetch() that retries once when CSRF verification fails.
  async fetch(url: string, options: RequestInit = {}) {
    const fullUrl = `${this.BASE_URL}${url}`;
    const opts: RequestInit = { ...options, credentials: 'include' };
    const accessToken = localStorage.getItem('access_token');

    if (accessToken) {
      const headers = new Headers(options.headers || {});
      headers.set('Authorization', `Bearer ${accessToken}`);
      const response = await fetch(fullUrl, { ...opts, headers });

      // 401 invalid_token means the backend rejected this Bearer (e.g. it was signed with a
      // SECRET_KEY that has since been rotated). The cookie/CSRF fallback can't run cross-site
      // on Safari, so a stale token would wedge every write. Send the user through /logout,
      // which clears the token and forces a fresh WordPress login that mints a token signed
      // with the current key.
      if (response.status === 401 && (await this.parseJsonBody(response)).code === 'invalid_token') {
        window.location.href = '/logout';
      }

      return response;
    }

    const headers = await this.getHeadersWithCsrf(options);
    const fullOpts: RequestInit = { ...opts, headers };
    let response = await fetch(fullUrl, fullOpts);

    // If the request failed due to missing/invalid CSRF, refresh and retry once.
    if (response.status === 403) {
      if ((await this.parseJsonBody(response)).message === 'CSRF verification failed.') {
        try {
          const newToken = await this.refreshCsrfToken();
          if (newToken) {
            headers.set('X-CSRFToken', newToken);
            const retryOpts: RequestInit = { ...fullOpts, headers };
            response = await fetch(fullUrl, retryOpts);
          }
        } catch (error) {
          console.error('Error when retrying fetch after bad CSRF error.', error);
        }
      }
    }

    return response;
  }

  private async parseJsonBody(response: Response): Promise<{ message?: string; code?: string }> {
    try {
      return await response.clone().json();
    } catch {
      return {};
    }
  }

  // Public method to fetch and return a fresh CSRF token. Also caches it on window.
  private async refreshCsrfToken(): Promise<string> {
    const response = await fetch(`${this.BASE_URL}/csrf-token`, {
      credentials: 'include',
      method: 'GET',
    });
    const { csrf_token } = await response.json();
    this.csrfToken = csrf_token as string;
    windowWrapper.set('csrf_token', csrf_token);
    return this.csrfToken;
  }

  private async getHeadersWithCsrf(options: RequestInit) {
    const method = (options.method || 'GET').toString().toUpperCase();
    const methodRequiresCsrfProtection = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
    const headers = new Headers(options.headers || {});

    // If we already have a CSRF token and the caller hasn't set it, attach it.
    if (this.csrfToken && !headers.has('X-CSRFToken')) {
      headers.set('X-CSRFToken', this.csrfToken);
    }

    // For the methods that require it, prefetch a token if missing to avoid a round-trip 403.
    if (methodRequiresCsrfProtection && !headers.has('X-CSRFToken')) {
      try {
        const token = await this.refreshCsrfToken();
        if (token) {
          headers.set('X-CSRFToken', token);
        }
      } catch {
        // If prefetch fails, proceed; we'll handle a 403 later if it happens.
      }
    }
    return headers;
  }
}

export function useApi(): Api {
  return useMemo(() => new Api(), []);
}

export default useApi;
